import { useState, useCallback } from 'react';
import { useIsMounted } from './useIsMounted';
import { extractContractError } from '@bafkal/ui-shared/utils';

/**
 * Custom hook for handling async operations with loading and error states
 * @param {Function} asyncFunction - The async function to execute
 * @returns {Object} { execute, loading, error, data }
 */
export const useAsync = (asyncFunction) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const isMounted = useIsMounted();

  const execute = useCallback(
    async (...args) => {
      try {
        if (isMounted.current) {
          setLoading(true);
          setError(null);
        }

        const result = await asyncFunction(...args);

        if (isMounted.current) {
          setData(result);
          return result;
        }
      } catch (err) {
        if (isMounted.current) {
          const contractError = extractContractError(err, 'An error occurred');
          setError(contractError.message);
          throw err;
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    },
    [asyncFunction, isMounted]
  );

  return { execute, loading, error, data };
};
